import { useCallback, useRef, useState } from "react";
import { isNonEmptyString, isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";
import { api } from "services";
import { COLUMN_HEADER_KEYS } from "globals/constants";
import { notifyError } from "utils";


const debuggerPrefix = "useTeamAccess >> ";

export const useTeamAccess = (initialTeam, formType, formId) => {
	const members = useRef(isNonEmptyArray(initialTeam)
		? [ ...initialTeam ]
		: []
	);
	const lastSavedTeam = useRef([ ...members.current ]);
	const [ isSaving, setIsSaving ] = useState(false);

	const getMemberIdFromPerson = person => {
		return isNonEmptyString(person)
			? person
			: person?.personId || person?.id || undefined;
	};

	const normalizePerson = person => {
		const combineNameParts = () => [ person.firstName, person.middleName, person.lastName ]
			.filter(name => isNonEmptyString(name))
			.join(" ");

		const fullName = isNonEmptyString(person.fullName)
			? person.fullName
			: combineNameParts();

		const personId = isNonEmptyString(person.personId)
			? person.personId
			: person.id;

		const email = person.contactEmail || person.email;

		return { personId, fullName, email };
	};

	const addMember = useCallback((person, { canEdit }) => {
		const newMember = normalizePerson(person);
		const existingMember = members.current.find(({ personId }) => personId === newMember.personId);

		if (!existingMember) {
			members.current.push({
				...newMember,
				canUpdate: !!canEdit
			});
		}
	}, [ members ]);

	const removeMember = useCallback(person => {
		const personIdToRemove = getMemberIdFromPerson(person);

		if (!personIdToRemove) {
			console.warn(`${debuggerPrefix} Asked to remove invalid team member: `, person);

			return;
		}

		members.current = members.current.filter(({ personId }) => personId !== personIdToRemove);
	}, [ members ]);

	const setCanMemberEdit = (person, canEdit) => {
		const personIdToUpdate = getMemberIdFromPerson(person);

		members.current = members.current.map(member => {
			return member.personId === personIdToUpdate
				? { ...member, canUpdate: canEdit }
				: { ...member };
		});
	};

	const save = useCallback(async () => {
		let request;
		switch (formType) {
			case COLUMN_HEADER_KEYS.PROPOSAL: request = api.proposal.updateAccess; break;
			case COLUMN_HEADER_KEYS.AWARD: request = api.awards.updateAccess; break;
			case COLUMN_HEADER_KEYS.PROJECT: request = api.project.updateAccess; break;
			default:
				throw new Error(`Invalid form type: ${formType}`);
		}

		setIsSaving(true);

		try {
			await request({ formId }, members.current);
			lastSavedTeam.current = [ ...members.current ];

		} catch (error) {
			// todo: handle error
			notifyError("Something went wrong. Please try again.");
			console.error(error);

		} finally {
			setIsSaving(false);
		}
	}, [ members, formType, formId ]);

	const resetChanges = useCallback(() => {
		members.current = [ ...lastSavedTeam.current ];
	}, [ lastSavedTeam ]);

	return {
		addMember,
		members: members.current,
		removeMember,
		resetChanges,
		save,
		isSaving,
		setCanMemberEdit
	};
};
